import React, { useCallback, useEffect, useRef, useState } from 'react';
import { conditionDescription } from '../../config/conditions';
import HoverCard from './HoverCard';

/**
 * The stored description is RICHTEXT — `<p>The character cannot see. …` — and
 * this card renders into a text node, so the markup has to come off first.
 * Without it a reader hovering "blinded" was shown a literal `<p>` (React
 * escapes the string, correctly, so the tag arrived as visible characters).
 *
 * DOMParser rather than a tag-stripping regex, because it also decodes the
 * entities the corpus carries (`&#39;`, `&amp;`) and gets nested markup right.
 * It parses without executing: `text/html` runs no script, and the result is
 * used as TEXT, never as HTML — the safe direction, so this is not a second
 * sanitiser and must not become one.
 */
export function plainText(html) {
  if (!html) return '';
  try {
    const doc = new DOMParser().parseFromString(String(html), 'text/html');
    return (doc.body.textContent || '').replace(/\s+/g, ' ').trim();
  } catch (err) {
    // No DOMParser (a non-browser renderer): strip tags and accept that an
    // entity may survive, rather than showing the markup.
    return String(html).replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
  }
}

/**
 * The first sentence of plain text, the short form a hover card shows (issue
 * 36's rule, shared with the entry card since issue 1017). A lookahead, never
 * a lookbehind: lookbehind is ES2018 and throws when the regex is CONSTRUCTED
 * on older Safari, which would take down the page rather than degrade one
 * feature. A sentence longer than `max` is cut at a word and marked.
 */
export function firstSentence(text, max = 240) {
  if (!text) return '';
  const first = ((text.match(/^[\s\S]*?\.(?=\s|$)/) || [text])[0]).trim();
  if (first.length <= max) return first;
  const cut = first.slice(0, max);
  const at = cut.lastIndexOf(' ');
  return `${(at > max / 2 ? cut.slice(0, at) : cut).replace(/[\s,;:]+$/, '')}…`;
}

/**
 * Raises a short description when a condition term in the wrapped prose is
 * hovered or focused (issue 36, phase 4b).
 *
 * ⚠️ THIS COMPONENT INJECTS NO HTML. `RichText` remains "the one place authored
 * HTML becomes DOM" — the comment on that file explains that three separate
 * `dangerouslySetInnerHTML` call sites are how all three came to be unsanitised
 * at once. So this takes the rendered prose as `children` and only listens on
 * it; adding a second injection site here would reintroduce exactly the risk
 * that convention exists to prevent.
 *
 * ⚠️ ONE DELEGATED CARD, NOT A TOOLTIP PER TERM. The obvious implementation
 * puts each description into the markup beside its term and shows it with CSS.
 * That would fold the full rules text of every mentioned condition into the
 * prose's own text content — changing `innerText` for anything that reads it,
 * and swallowing the description into each link's accessible name. Here the
 * annotator emits nothing but an anchor and the description is looked up in JS,
 * so the prose reads exactly as it did before, with its terms linked.
 *
 * The card is never the only affordance: each term is a real link to the
 * condition's page, so a reader with no pointer, no hover or no JavaScript
 * still gets there.
 */
export default function ConditionCardLayer({ conditions, children }) {
  const ref = useRef(null);
  const [tip, setTip] = useState(null);

  const show = useCallback((el) => {
    const slug = el.getAttribute('data-condition');
    if (!slug || !ref.current) return;
    const hit = (conditions || []).find((c) => c.slug === slug);
    const full = plainText(conditionDescription(slug));
    // The short form is the FIRST SENTENCE of the verbatim SRD text. No
    // `summary` field was authored, deliberately (design note §5), and the
    // SRD's opening sentence is already the mechanical core:
    // "A shaken character takes a −2 penalty on attack rolls, …". Uncapped,
    // as it always was: a condition's opening sentence is short.
    const first = firstSentence(full, Infinity);
    const box = ref.current.getBoundingClientRect();
    const r = el.getBoundingClientRect();
    setTip({
      name: (hit && hit.name) || slug,
      text: first.trim(),
      top: r.bottom - box.top + 6,
      left: Math.max(0, r.left - box.left),
    });
  }, [conditions]);

  const hide = useCallback(() => setTip(null), []);

  useEffect(() => {
    const root = ref.current;
    if (!root) return undefined;

    const enter = (e) => {
      const el = e.target && e.target.closest && e.target.closest('a.condition-term');
      if (el) show(el);
    };
    const leave = (e) => {
      const el = e.target && e.target.closest && e.target.closest('a.condition-term');
      if (el) hide();
    };
    // Escape dismisses without moving focus. A card that can only be closed by
    // moving the pointer is unusable for a keyboard reader.
    const key = (e) => { if (e.key === 'Escape') hide(); };

    root.addEventListener('mouseover', enter);
    root.addEventListener('mouseout', leave);
    root.addEventListener('focusin', enter);
    root.addEventListener('focusout', leave);
    root.addEventListener('keydown', key);
    return () => {
      root.removeEventListener('mouseover', enter);
      root.removeEventListener('mouseout', leave);
      root.removeEventListener('focusin', enter);
      root.removeEventListener('focusout', leave);
      root.removeEventListener('keydown', key);
    };
  }, [show, hide]);

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      {children}
      {tip && (
        <HoverCard
          className="condition-card"
          name={tip.name}
          text={tip.text}
          hint="Opens in a new tab"
          style={{ top: tip.top, left: tip.left }}
        />
      )}
    </div>
  );
}
